import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnDestroy, OnInit, output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { ActivatedRoute, Router } from "@angular/router";
import { ConfirmationDialogComponent } from "../../../utils/confirmation-dialog/confirmation-dialog.component";
import { SystemAdminService } from "../../../../services/system-admin.service";
import { BranchContactResponse } from "../../../../models/SystemAdmin.model";
import { Subscription } from "rxjs";
import { CountryISO, NgxIntlTelInputModule, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

@Component({
    selector: 'app-branch-contact-details',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, MatDialogModule, ReactiveFormsModule, NgxIntlTelInputModule],
    templateUrl: './branch-contact-details.component.html',
    styleUrl: './branch-contact-details.component.scss'
})
export class BranchContactDetails implements OnInit, OnDestroy{
    @Input() branchContactForm!: FormGroup;
    @Input() isCreateBranch!: boolean;
    // @Input() formatPhoneNumber : (phoneNumber: string | null) => string | null = (phoneNumber: string | null) => null;
    @Input() extractRawPhoneNumber : (phoneNumber: string | null) => string | null = (phoneNumber: string | null) => null;
    @Input() countryId: string | null = null;

    // countryId: number | null = null;

    branchContactDetailsSubmitEmitter = output<boolean>();
    router = inject(Router);
    activateRoute = inject(ActivatedRoute);
    dialog = inject(MatDialog);
    systemAdminService = inject(SystemAdminService);
    phoneInputSubscription: Subscription = new Subscription();
    CountryISO = CountryISO;
    SearchCountryField = SearchCountryField;
    phoneUtil = PhoneNumberUtil.getInstance();
    
    // Get all countries from PhoneNumberUtil for dropdown
    getAllCountries(): { code: CountryISO; name: string; displayName: string }[] {
      const countries: { code: CountryISO; name: string; displayName: string }[] = [];
      try {
        const supportedRegions = this.phoneUtil.getSupportedRegions();
        for (const region of supportedRegions) {
          try {
            const countryCode = region as CountryISO;
            const countryCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
            const countryName = this.getCountryName(countryCode);
            const displayName = countryCallingCode ? `+${countryCallingCode} ${countryName}` : countryName;
            countries.push({ code: countryCode, name: countryName, displayName });
          } catch {
            // Skip invalid regions
          }
        }
      } catch {
        countries.push(
          { code: CountryISO.UnitedStates, name: 'United States', displayName: '+1 United States' },
          { code: CountryISO.UnitedKingdom, name: 'United Kingdom', displayName: '+44 United Kingdom' }
        );
      }
      return countries.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    getCountryName(countryCode: CountryISO): string {
      const codeStr = String(countryCode).toUpperCase();
      try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const countryName = displayNames.of(codeStr);
        if (countryName) {
          return countryName;
        }
      } catch (error) {
        // Fallback to hardcoded mapping
      }
      const countryNames: { [key: string]: string } = {
        'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada', 'AU': 'Australia',
        'FR': 'France', 'DE': 'Germany', 'IT': 'Italy', 'ES': 'Spain', 'NL': 'Netherlands',
        'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway',
        'DK': 'Denmark', 'FI': 'Finland', 'PL': 'Poland', 'CZ': 'Czech Republic', 'IE': 'Ireland',
        'PT': 'Portugal', 'GR': 'Greece', 'LU': 'Luxembourg', 'IS': 'Iceland', 'MT': 'Malta',
        'CY': 'Cyprus', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania', 'SI': 'Slovenia',
        'SK': 'Slovakia', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'HR': 'Croatia'
      };
      if (countryNames[codeStr]) {
        return countryNames[codeStr];
      }
      return codeStr.split('').map((char, i) => i === 0 ? char.toUpperCase() : char.toLowerCase()).join('');
    }

    getMaxPhoneLength(countryCode: CountryISO | null | undefined): number {
      if (!countryCode) return 15;
      const regionCode = String(countryCode).toLowerCase();
      
      // Comprehensive country-to-max-length mapping
      const countryMaxLengths: { [key: string]: number } = {
        // Very short numbers (4-5 digits)
        'nu': 4,  // Niue
        'tk': 4,  // Tokelau
        'nf': 5,  // Norfolk Island
        
        // Short numbers (6-7 digits)
        'gi': 8,  // Gibraltar
        'va': 8,  // Vatican City
        'sm': 9,  // San Marino
        'mc': 9,  // Monaco
        'li': 9,  // Liechtenstein
        
        // Standard 10-digit numbers (US, Canada, etc.)
        'us': 10, // United States
        'ca': 10, // Canada
        'ru': 10, // Russia
        'kz': 10, // Kazakhstan
        'mx': 10, // Mexico
        'br': 11, // Brazil (can be 10 or 11)
        'in': 10, // India
        'au': 9,  // Australia
        'nz': 9,  // New Zealand
        'za': 9,  // South Africa
        'ar': 10, // Argentina
        'cl': 9,  // Chile
        'co': 10, // Colombia
        'pe': 9,  // Peru
        've': 10, // Venezuela
        'ec': 9,  // Ecuador
        'uy': 8,  // Uruguay
        'py': 9,  // Paraguay
        'bo': 8,  // Bolivia
        'cr': 8,  // Costa Rica
        'pa': 8,  // Panama
        'gt': 8,  // Guatemala
        'hn': 8,  // Honduras
        'ni': 8,  // Nicaragua
        'sv': 8,  // El Salvador
        'do': 10, // Dominican Republic
        'cu': 8,  // Cuba
        'jm': 10, // Jamaica
        'tt': 10, // Trinidad and Tobago
        'bb': 10, // Barbados
        'bs': 10, // Bahamas
        'bz': 7,  // Belize
        'gy': 7,  // Guyana
        'sr': 7,  // Suriname
        'gf': 9,  // French Guiana
        
        // 11-digit numbers (UK, etc.)
        'gb': 11, // United Kingdom
        'ie': 9,  // Ireland
        'nl': 9,  // Netherlands
        'be': 9,  // Belgium
        'lu': 9,  // Luxembourg
        'ch': 9,  // Switzerland
        'at': 10, // Austria
        'de': 11, // Germany
        'dk': 8,  // Denmark
        'se': 9,  // Sweden
        'no': 8,  // Norway
        'fi': 9,  // Finland
        'is': 7,  // Iceland
        'pl': 9,  // Poland
        'cz': 9,  // Czech Republic
        'sk': 9,  // Slovakia
        'hu': 9,  // Hungary
        'ro': 10, // Romania
        'bg': 9,  // Bulgaria
        'gr': 10, // Greece
        'pt': 9,  // Portugal
        'es': 9,  // Spain
        'it': 10, // Italy
        'mt': 8,  // Malta
        'cy': 8,  // Cyprus
        
        // Eastern Europe
        'ua': 9,  // Ukraine
        'by': 9,  // Belarus
        'lt': 8,  // Lithuania
        'lv': 8,  // Latvia
        'ee': 8,  // Estonia
        'md': 8,  // Moldova
        'rs': 9,  // Serbia
        'hr': 9,  // Croatia
        'si': 8,  // Slovenia
        'ba': 8,  // Bosnia and Herzegovina
        'mk': 9,  // North Macedonia
        'al': 9,  // Albania
        'me': 8,  // Montenegro
        'xk': 8,  // Kosovo
        
        // Middle East
        'sa': 9,  // Saudi Arabia
        'ae': 9,  // UAE
        'kw': 8,  // Kuwait
        'qa': 8,  // Qatar
        'bh': 8,  // Bahrain
        'om': 8,  // Oman
        'ye': 9,  // Yemen
        'iq': 10, // Iraq
        'ir': 10, // Iran
        'il': 9,  // Israel
        'jo': 9,  // Jordan
        'lb': 8,  // Lebanon
        'sy': 9,  // Syria
        'ps': 9,  // Palestine
        'tr': 10, // Turkey
        'eg': 10, // Egypt
        'ly': 9,  // Libya
        'tn': 8,  // Tunisia
        'dz': 9,  // Algeria
        'ma': 9,  // Morocco
        'sd': 9,  // Sudan
        'et': 9,  // Ethiopia
        
        // Asia
        'cn': 11, // China
        'jp': 10, // Japan
        'kr': 10, // South Korea
        'tw': 9,  // Taiwan
        'hk': 8,  // Hong Kong
        'mo': 8,  // Macau
        'sg': 8,  // Singapore
        'my': 9,  // Malaysia
        'th': 9,  // Thailand
        'vn': 10, // Vietnam
        'ph': 10, // Philippines
        'id': 9,  // Indonesia
        'mm': 9,  // Myanmar
        'kh': 9,  // Cambodia
        'la': 8,  // Laos
        'bn': 7,  // Brunei
        'bd': 10, // Bangladesh
        'pk': 10, // Pakistan
        'lk': 9,  // Sri Lanka
        'np': 10, // Nepal
        'af': 7,  // Afghanistan
        'uz': 9,  // Uzbekistan
        'tm': 8,  // Turkmenistan
        'tj': 9,  // Tajikistan
        'kg': 9,  // Kyrgyzstan
        'mn': 8,  // Mongolia
        
        // Oceania
        'pg': 8,  // Papua New Guinea
        'fj': 7,  // Fiji
        'nc': 6,  // New Caledonia
        'pf': 6,  // French Polynesia
        'ws': 7,  // Samoa
        'to': 7,  // Tonga
        'vu': 7,  // Vanuatu
        'sb': 7,  // Solomon Islands
        'ki': 5,  // Kiribati
        'tv': 6,  // Tuvalu
        'nr': 7,  // Nauru
        'pw': 7,  // Palau
        'fm': 7,  // Micronesia
        'mh': 7,  // Marshall Islands
        
        // Africa
        'ng': 10, // Nigeria
        'ke': 9,  // Kenya
        'tz': 9,  // Tanzania
        'ug': 9,  // Uganda
        'rw': 9,  // Rwanda
        'gh': 9,  // Ghana
        'ci': 8,  // Côte d'Ivoire
        'sn': 9,  // Senegal
        'ml': 8,  // Mali
        'bf': 8,  // Burkina Faso
        'ne': 8,  // Niger
        'td': 8,  // Chad
        'cm': 9,  // Cameroon
        'ga': 8,  // Gabon
        'cg': 9,  // Republic of the Congo
        'cd': 9,  // DR Congo
        'ao': 9,  // Angola
        'zm': 9,  // Zambia
        'zw': 9,  // Zimbabwe
        'bw': 8,  // Botswana
        'na': 9,  // Namibia
        'mw': 9,  // Malawi
        'mz': 9,  // Mozambique
        'mg': 9,  // Madagascar
        'mu': 8,  // Mauritius
        'sc': 7,  // Seychelles
        'km': 7,  // Comoros
        'dj': 8,  // Djibouti
        'er': 7,  // Eritrea
        'so': 8,  // Somalia
        'ss': 9,  // South Sudan
        
        // Caribbean
        'ht': 8,  // Haiti
        'pr': 10, // Puerto Rico
        'vi': 10, // US Virgin Islands
        'vg': 10, // British Virgin Islands
        'ky': 7,  // Cayman Islands
        'bm': 10, // Bermuda
        'tc': 7,  // Turks and Caicos
        'ai': 7,  // Anguilla
        'ms': 7,  // Montserrat
        'gd': 7,  // Grenada
        'lc': 7,  // Saint Lucia
        'vc': 7,  // Saint Vincent
        'ag': 10,  // Antigua and Barbuda
        'dm': 7,  // Dominica
        'kn': 7,  // Saint Kitts and Nevis
        
        // Other territories
        'fo': 6,  // Faroe Islands
        'gl': 6,  // Greenland
        'sj': 8,  // Svalbard
        'ax': 6,  // Åland Islands
        'gg': 10, // Guernsey
        'je': 10, // Jersey
        'im': 10, // Isle of Man
        'ad': 6,  // Andorra
      };
      
      return countryMaxLengths[regionCode] || 15; // Default to 15 for unknown countries
    }
    
    onContactPersonPhoneCountryChange(country: any) {
      if (country && country.iso2) {
        this.branchContactForm.get('contactPersonPhoneCountryCode')?.setValue(country.iso2, { emitEvent: true });
        // Reset phone number field when country changes
        this.branchContactForm.get('contactPersonPhone')?.setValue('', { emitEvent: false });
      }
    }
    
    // Convert calling code string (e.g., "+93") to CountryISO
    getCountryCodeFromCountryId(countryId: number | null): CountryISO | null {
      if (!countryId) return null;
      // Map countryId to CountryISO
      // Assuming countryId 1 = US, countryId 2 = UK
      if (countryId === 1) return CountryISO.UnitedStates;
      if (countryId === 2) return CountryISO.UnitedKingdom;
      
      // Try to find country in country list if available
      // Note: This component might not have access to countryList, so we use basic mapping
      return null;
    }
    
    mapCallingCodeToISO(callingCode: string | number | null): CountryISO | null {
      if (!callingCode) return null;
      
      const codeStr = String(callingCode).replace(/^\+/, '');
      const codeNum = parseInt(codeStr, 10);
      
      if (isNaN(codeNum)) return null;
      
      try {
        const supportedRegions = this.phoneUtil.getSupportedRegions();
        for (const region of supportedRegions) {
          try {
            const regionCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
            if (regionCallingCode === codeNum) {
              // Convert to uppercase to match CountryISO enum values
              return region.toUpperCase() as CountryISO;
            }
          } catch {
            // Skip invalid regions
          }
        }
      } catch {
        // Fallback mappings
      }
      
      if (codeNum === 1) return CountryISO.UnitedStates;
      if (codeNum === 44) return CountryISO.UnitedKingdom;
      if (codeNum === 93) return CountryISO.Afghanistan;
      
      return null;
    }

    ngOnInit(): void {
      this.activateRoute.params.subscribe({
        next: (params) => {
          if(params && params['branchId']){
            this.branchContactForm?.markAsUntouched();
            this.fetchBranchContactDetails(params['branchId']);
          }
        }
      });
    }

    fetchBranchContactDetails(branchId: string) {
      this.systemAdminService.getBranchContactDetails(branchId).subscribe({
        next: (response: BranchContactResponse) => {
          if(response){
            this.onBindFormData(response);
          }
        },
        error: (error: any) => {
        }
      })
    }

    onBindFormData(formValue: any){
      this.branchContactForm.patchValue({
        contactPersonName: formValue.contactPersonName,
        contactPersonRole: formValue.contactPersonRole,
        contactPersonEmail: formValue.contactPersonEmail
      });

      const contactPersonPhone = formValue?.contactPersonPhone ?? formValue?.contactPersonIPhone?.replace(/^\+\d+/, '') ?? null;
      const contactCountryCodeFromResponse = formValue?.contactCountryCode ?? null;
      
      // Get ISO2 code from API response (prioritize this over calling codes)
      // Check both uppercase and lowercase property names for compatibility
      const contactPhoneCodeFromResponse = formValue?.ContactPhoneCode ?? formValue?.contactPhoneCode ?? null;
      
      // Check if phone codes are missing (for existing records)
      const phoneCodesMissing = !contactPhoneCodeFromResponse && !contactCountryCodeFromResponse;
      
      // Convert ISO2 code to CountryISO enum (if provided)
      // IMPORTANT: Use ISO2 codes directly from API response to avoid ambiguity
      let contactPhoneCodeISO: CountryISO | null = null;
      
      if (contactPhoneCodeFromResponse) {
        const iso2Upper = String(contactPhoneCodeFromResponse).toUpperCase().trim();
        // Directly use the ISO2 code as CountryISO (CountryISO enum values are ISO2 codes)
        // This ensures we use the exact ISO2 code from API response (e.g., "AD" for Andorra)
        if (iso2Upper && iso2Upper.length === 2) {
          contactPhoneCodeISO = iso2Upper as CountryISO;
        }
      }
      
      // If phone codes are missing and we have countryId from branch-details, use it to determine phone country code
      if (phoneCodesMissing && this.countryId) {
        const countryIdNum = this.countryId ? Number(this.countryId) : null;
        const countryCodeFromCountryId = this.getCountryCodeFromCountryId(countryIdNum);
        if (countryCodeFromCountryId) {
          // Use countryId from branch-details to set phone code
          contactPhoneCodeISO = countryCodeFromCountryId;
        }
      }
      
      // Use ISO2 code directly if available, otherwise fall back to calling code conversion
      // CRITICAL: Always prioritize ISO2 codes from API response to avoid ambiguity
      const contactPersonPhoneCountryCode = contactPhoneCodeISO ?? 
        (contactCountryCodeFromResponse ? this.mapCallingCodeToISO(String(contactCountryCodeFromResponse)) : null) ??
        (this.countryId ? this.getCountryCodeFromCountryId(Number(this.countryId)) : null) ??
        CountryISO.UnitedStates;
      
      // Set phone country code FIRST (before formatting phone numbers)
      // CRITICAL: If ISO2 code is available from API response, use it directly
      if (contactPhoneCodeISO) {
        // Use ISO2 code directly from API response
        this.branchContactForm.get('contactPersonPhoneCountryCode')?.setValue(contactPhoneCodeISO, { emitEvent: false });
      } else {
        // Fallback to calling code conversion only if ISO2 is not available
        this.branchContactForm.get('contactPersonPhoneCountryCode')?.setValue(contactPersonPhoneCountryCode, { emitEvent: false });
      }
      
      // Ensure the ISO2 code is set after a brief delay to allow ngx-intl-tel-input to initialize
      if (contactPhoneCodeISO) {
        setTimeout(() => {
          this.branchContactForm.get('contactPersonPhoneCountryCode')?.setValue(contactPhoneCodeISO, { emitEvent: false });
        }, 100);
      }
      
      // Set contactCountryCode from response if available
      if (contactCountryCodeFromResponse) {
        this.branchContactForm.get('contactCountryCode')?.setValue(String(contactCountryCodeFromResponse));
      }
      
      // Format phone number AFTER country code is set
      setTimeout(() => {
        if (contactPersonPhone !== null) {
          const formattedPhone = this.formatPhoneNumberForBinding(String(contactPersonPhone), contactPersonPhoneCountryCode);
          this.branchContactForm.get('contactPersonPhone')?.setValue(formattedPhone);
        }
      }, 0);
    }
    
    private formatPhoneNumberForBinding(phoneNumber: string | null, countryCode: CountryISO | null): string | null {
      if (!phoneNumber || typeof phoneNumber !== "string") {
        return null;
      }
      
      // Extract raw digits first
      let raw = this._extractRawPhoneNumber(phoneNumber, countryCode);
      
      // Special handling for UK: if 11 digits, cut the last digit (UK numbers should be 10 digits for mobile or 11 with leading 0)
      if (countryCode === CountryISO.UnitedKingdom && raw && raw.length === 11 && !raw.startsWith('0')) {
        raw = raw.substring(0, 10);
      }
      
      // Format the phone number directly - formatPhoneNumber will handle parsing and formatting
      // It will extract raw digits and format according to the country code
      return this.formatPhoneNumber(raw, countryCode);
    }
    
    private _extractRawPhoneNumber(formattedPhoneNumber: string | null | undefined, countryCode?: CountryISO | null): string | null {
      if (typeof formattedPhoneNumber !== 'string') {
        return null;
      }
      
      // Extract all digits first
      let cleaned = formattedPhoneNumber.replace(/\D/g, '');
      
      if (!cleaned || cleaned.length === 0) {
        return null;
      }
      
      if (!countryCode) {
        return cleaned;
      }

      // Try to parse and extract national number
      try {
        // Convert CountryISO to lowercase string region code for phoneUtil.parse()
        const regionCode = countryCode ? String(countryCode).toLowerCase() : undefined;
        if (!regionCode) {
          return cleaned;
        }
        const parsedNumber = this.phoneUtil.parse(cleaned, regionCode);
        
        // Get national number (without country code)
        const nationalNumber = parsedNumber.getNationalNumber();
        if (nationalNumber) {
          return String(nationalNumber);
        }
      } catch (error) {
        // Parsing failed - might be incomplete number, try to remove country code prefix manually
      }
      
      // Fallback: remove country code prefix if present
      const countryCodePrefix = this.getCountryCodePrefix(countryCode);
      if (countryCodePrefix) {
        const prefixDigits = countryCodePrefix.replace(/\D/g, '');
        // Check if the cleaned number starts with the country code prefix
        if (cleaned.startsWith(prefixDigits)) {
          // Remove the prefix to get national number
          cleaned = cleaned.substring(prefixDigits.length);
          // For UK (44), also remove leading 0 if present (UK national format starts with 0)
          if (countryCode === CountryISO.UnitedKingdom && cleaned.startsWith('0')) {
            cleaned = cleaned.substring(1);
          }
        }
      }
      
      return cleaned;
    }
    
    private getCountryCodePrefix(countryCode: CountryISO | null | undefined): string {
      if (!countryCode) {
        return '';
      }
      try {
        // Convert CountryISO to lowercase string region code for phoneUtil.getCountryCodeForRegion()
        const regionCode = String(countryCode).toLowerCase();
        const callingCode = this.phoneUtil.getCountryCodeForRegion(regionCode);
        return callingCode ? `+${callingCode}` : '';
      } catch {
        return '';
      }
    }
    
    private formatPhoneNumber(phoneNumber: string | null, countryCode: CountryISO | null): string | null {
      if (!phoneNumber || typeof phoneNumber !== "string") {
        return null;
      }
      
      if (!countryCode) {
        return phoneNumber;
      }
      
      try {
        // Remove all non-digit characters for parsing
        const cleanedNumber = phoneNumber.replace(/\D/g, '');
        
        // Special handling for UK to ensure consistent formatting - ALWAYS use this for UK
        if (countryCode === CountryISO.UnitedKingdom) {
          // UK numbers: 10 digits (mobile) or 11 digits (landline)
          if (cleanedNumber.length <= 4) {
            return cleanedNumber;
          } else if (cleanedNumber.length <= 7) {
            return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
          } else if (cleanedNumber.length === 10) {
            // 10 digits: XXXX XXXXXX (e.g., 7655 677666)
            return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
          } else if (cleanedNumber.length === 11) {
            // 11 digits: XXXX XXX XXXX (e.g., 7655 677 6666)
            return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
          } else {
            // More than 11 digits: truncate to 11 and format
            const truncated = cleanedNumber.substring(0, 11);
            return `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7, 11)}`;
          }
        }
        
        // Convert CountryISO to lowercase string region code for phoneUtil.parse()
        const regionCode = String(countryCode).toLowerCase();
        
        // Parse the phone number with the selected country code
        const parsedNumber = this.phoneUtil.parse(cleanedNumber, regionCode);
        
        // Format as national number (without country code) since country code is displayed separately
        try {
          const formatted = this.phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL);
          
          // Remove any leading country code that might be included
          let finalFormatted = formatted.replace(/^\+\d+\s*/, '').trim();
          // Remove leading "0" that NATIONAL format adds for some countries (e.g., Afghanistan, UK)
          // Since country code is displayed separately, we don't need this leading "0"
          finalFormatted = finalFormatted.replace(/^0+/, '').trim();
          return finalFormatted;
        } catch (formatError) {
          const progressiveResult = this.formatPhoneNumberProgressive(cleanedNumber, countryCode);
          return progressiveResult;
        }
      } catch (error) {
        const fallbackResult = this.formatPhoneNumberFallback(phoneNumber, countryCode);
        return fallbackResult;
      }
    }
    
    private formatPhoneNumberProgressive(cleanedNumber: string, countryCode: CountryISO | null): string {
      if (!countryCode || !cleanedNumber) {
        return cleanedNumber;
      }
      
      // Format progressively based on country
      if (countryCode === CountryISO.UnitedStates) {
        // US format: (XXX) XXX-XXXX
        if (cleanedNumber.length <= 3) {
          return cleanedNumber.length > 0 ? `(${cleanedNumber}` : '';
        } else if (cleanedNumber.length <= 6) {
          return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3)}`;
        } else {
          return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3, 6)}-${cleanedNumber.slice(6, 10)}`;
        }
      } else if (countryCode === CountryISO.UnitedKingdom) {
        // UK format: XXXX XXX XXXX or variations
        if (cleanedNumber.length <= 4) {
          return cleanedNumber;
        } else if (cleanedNumber.length <= 7) {
          return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
        } else {
          return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
        }
      }
      
      // Default: return as is
      return cleanedNumber;
    }
    
    private formatPhoneNumberFallback(phoneNumber: string, countryCode: CountryISO | null | undefined): string {
      if (!countryCode) {
        return phoneNumber;
      }
      
      const cleanedNumber = phoneNumber.replace(/\D/g, '');
      
      // Get country code prefix
      const countryCodePrefix = this.getCountryCodePrefix(countryCode);

      // Remove country code prefix if already present
      let digits = cleanedNumber;
      if (countryCodePrefix) {
        const prefixDigits = countryCodePrefix.replace(/\D/g, '');
        if (digits.startsWith(prefixDigits)) {
          digits = digits.substring(prefixDigits.length);
        }
      }

      // Format based on country (without country code prefix since it's displayed separately)
      if (countryCode === CountryISO.UnitedStates && digits.length === 10) {
        const match = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
      } else if (countryCode === CountryISO.UnitedStates && digits.length > 0) {
        // Progressive formatting for US
        return this.formatPhoneNumberProgressive(digits, countryCode);
      } else if (countryCode === CountryISO.UnitedKingdom && digits.length >= 10) {
        // UK numbers can vary, try common format
        if (digits.length === 11) {
          const match = digits.match(/^(\d{4})(\d{3})(\d{4})$/);
          if (match) {
            return `${match[1]} ${match[2]} ${match[3]}`;
          }
        }
        // Progressive formatting for UK
        return this.formatPhoneNumberProgressive(digits, countryCode);
      }

      return digits;
    }

    private formatPhoneNumberWithCountryCode(phoneNumber: string | null, countryCode: string | null, countryId: number | null): string | null {
      if (!phoneNumber || typeof phoneNumber !== "string") {
        return null;
      }

      // Use countryCode from form if available
      let countryCodePrefix = countryCode ? String(countryCode) : '';

      // Remove country code prefix if already present
      let cleanedNumber = phoneNumber.replace(/\D/g, ''); // Remove non-numeric characters
      if (countryCodePrefix) {
        const prefixDigits = countryCodePrefix.replace(/\D/g, '');
        if (prefixDigits && cleanedNumber.startsWith(prefixDigits)) {
          cleanedNumber = cleanedNumber.substring(prefixDigits.length);
        }
      }

      // If the number has fewer digits than required for a valid format, return with prefix if available
      if ((countryId == 1 && cleanedNumber.length < 10) || (countryId == 2 && cleanedNumber.length < 11)) {
        return countryCodePrefix ? `${countryCodePrefix} ${cleanedNumber}` : cleanedNumber;
      }

      // Format for USA (countryId 1) - +1 (XXX) XXX-XXXX
      if (countryId == 1 && cleanedNumber.length === 10) {
        const match = cleanedNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          return countryCodePrefix ? `${countryCodePrefix} (${match[1]}) ${match[2]}-${match[3]}` : `(${match[1]}) ${match[2]}-${match[3]}`;
        }
      } 
      // Format for UK (countryId 2) - +44 XXX XXX XXXX
      else if (countryId == 2 && cleanedNumber.length === 11) {
        const match = cleanedNumber.match(/^(\d{3})(\d{4})(\d{4})$/);
        if (match) {
          return countryCodePrefix ? `${countryCodePrefix} ${match[1]} ${match[2]} ${match[3]}` : `${match[1]} ${match[2]} ${match[3]}`;
        }
      }

      // Return original with prefix if available
      return countryCodePrefix ? `${countryCodePrefix} ${phoneNumber}` : phoneNumber; 
    }


    // Progressive live formatter for contactPersonPhone based on current countryId
    formatPhoneNumberLive(inputValue: any): string {
      const raw = (inputValue ?? '').toString();
      // Remove country code prefix if present
      let digits = raw.replace(/\D/g, '');
      const countryId = this.countryId ? Number(this.countryId) : null;
      
      // Get country code prefix
      const countryCodePrefix = this.branchContactForm.get('contactCountryCode')?.value ?? null;
      const countryCode = countryCodePrefix ? String(countryCodePrefix) : '';
      
      // Remove country code prefix if already present
      if (countryCode) {
        const prefixDigits = countryCode.replace(/\D/g, '');
        if (digits.startsWith(prefixDigits)) {
          digits = digits.substring(prefixDigits.length);
        }
      }
      
      // Limit digits based on country
      if (countryId == 1 && digits.length > 10) {
        digits = digits.substring(0, 10);
      } else if (countryId == 2 && digits.length > 11) {
        digits = digits.substring(0, 11);
      }
      
      if (!digits) return countryCode ? countryCode + ' ' : '';
      
      if (countryId == 1) {
        const a = digits.slice(0, 3);
        const b = digits.slice(3, 6);
        const c = digits.slice(6, 10);
        const formatted = digits.length <= 3 ? `(${a}` : digits.length <= 6 ? `(${a}) ${b}` : `(${a}) ${b}-${c}`;
        return countryCode ? `${countryCode} ${formatted}` : formatted;
      } else if (countryId == 2) {
        const a = digits.slice(0, 3);
        const b = digits.slice(3, 7);
        const c = digits.slice(7, 11);
        const formatted = digits.length <= 3 ? a : digits.length <= 7 ? `${a} ${b}` : `${a} ${b} ${c}`;
        return countryCode ? `${countryCode} ${formatted}` : formatted;
      }
      return countryCode ? `${countryCode} ${digits}` : digits;
    }

    onPhoneNumberInput(event: any) {
      const formattedNumber = this.formatPhoneNumberLive(event.target.value);
      this.branchContactForm.get('contactPersonPhone')?.setValue(formattedNumber, { emitEvent: false });
    }

    ngOnDestroy(): void {
      this.phoneInputSubscription.unsubscribe();
    }    

    
    onCancel(): void{
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
          data: { message: "Unsaved changes will be lost if you navigate from this page. Do you want to continue?" }
        });
    
        dialog.afterClosed().subscribe((result: boolean) => {
          if(result){
            this.branchContactForm.reset();
            this.router.navigate(['/system-admin-dashboard/branch']);
          }
        })
    }

    onSubmit() {
      this.branchContactDetailsSubmitEmitter.emit(true);
    }
}   

